/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import memoize from 'lodash/memoize';
import sortBy from 'lodash/sortBy';
import { ScrollToHelper, SecurityHelper } from 'sarsaparilla';
import download from 'downloadjs';
import { now, parseAbsolute, today } from '@internationalized/date';
import { INTERAGENCY_DISCOUNT_PASSES, PRICE_MAGNITUDE } from '../constants';
import { findPricingPeriodV2, isValidDateFormat, parseDate } from './date_time_utils';
import { apiHelpers } from './api-helpers';

export function memoizeDebounce(func, wait = 0, options = {}) {
    const mem = memoize(() => debounce(func, wait, options), options.resolver);
    return (...args) => {
        mem.apply(this, args).apply(this, args);
    };
}

export function capitalize(str = '') {
    return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
}

export const scrollUp = ({ top = 0, left = 0 } = {}) =>
    ScrollToHelper.scrollTo(top, left);

export const isFirefox = () => navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

export const getDivision = (periods = [], startDate, allowPast = false) => {
    const sortedPeriods = sortBy(periods, 'start_date');
    const todayCalendarDate = today();
    let startCalendarDate = todayCalendarDate;
    if (startDate && isValidDateFormat(startDate)) {
        startCalendarDate = parseDate(startDate);
    }
    const lowerbound =
        startCalendarDate.compare(todayCalendarDate) < 0
            ? todayCalendarDate
            : startCalendarDate;
    const activePeriods = allowPast
        ? sortedPeriods
        : sortedPeriods.filter(
              (x) => parseDate(x.start_date).year >= todayCalendarDate.year
          );

    let division = get(
        activePeriods.find(({ start_date }) => {
            return parseDate(start_date).compare(lowerbound) <= 0;
        }),
        'divisions',
        []
    ).find(
        ({ starts_at, ends_at }) =>
            parseDate(`${todayCalendarDate.year}-${starts_at}`).compare(lowerbound) <=
                0 &&
            parseDate(`${todayCalendarDate.add({ days: 1 }).year}-${ends_at}`).compare(
                lowerbound
            ) >= 0
    );

    // Show future pricing if available.
    if (!division && get(sortedPeriods, '[0].divisions.length')) {
        division = sortedPeriods[0].divisions[0];
    }

    return division;
};

export const getUnitCardinality = (value, singularNoun, pluralNoun) => {
    return value === 1 ? singularNoun : pluralNoun;
};

export const formatPrice = (value = 0) => `$${(value / PRICE_MAGNITUDE).toFixed(2)}`;

export const getPrice = (periods, startDate, allowPast = false) =>
    get(getDivision(periods, startDate, allowPast), 'display_price', 0) ||
    get(getDivision(periods, '2010-01-01', true), 'display_price', 0) ||
    formatPrice(
        get(getDivision(periods, startDate, allowPast), 'price', 0) ||
            get(getDivision(periods, '2010-01-01', true), 'price', 0)
    );

export const getSeasonDates = (periods, startDate, useAPDateFix = false) => {
    const period = findPricingPeriodV2(periods, startDate || today());
    if (!period) {
        return ['', ''];
    }
    const startCalendarDate = isValidDateFormat(startDate)
        ? parseDate(startDate)
        : today();
    const startMonthDay = `${startCalendarDate.month < 10 ? '0' : ''}${startCalendarDate.month}-${startCalendarDate.day < 10 ? '0' : ''}${startCalendarDate.day}`;
    let closest = null; // used if season hasn't started yet
    let first = null; // if we are after season end, we use next year first season
    const currentYear = startCalendarDate.year;
    const periodCalendarDate = isValidDateFormat(period.start_date)
        ? parseDate(period.start_date)
        : today();
    const periodMonthDay = `${periodCalendarDate.month < 10 ? '0' : ''}${periodCalendarDate.month}-${periodCalendarDate.day < 10 ? '0' : ''}${periodCalendarDate.day}`;

    for (const div of period.divisions) {
        // calculate season start year
        let seasonYear = currentYear;

        // feature flagged to use date calculation fix
        if (!useAPDateFix) {
            if (
                div.starts_at > div.ends_at && // cross year season
                startMonthDay < div.ends_at // season hasn't ended yet
            ) {
                seasonYear -= 1; // season started last year
            }
        } else {
            if (
                div.starts_at < div.ends_at && // normal single year season
                startMonthDay < periodMonthDay // season hasn't started yet
            ) {
                seasonYear -= 1; // pull previous season instead
            }

            if (
                div.starts_at > div.ends_at && // cross year season
                (startMonthDay <= div.ends_at || // season hasn't ended yet
                    startMonthDay < periodMonthDay) // season hasn't started yet
            ) {
                seasonYear -= 1; // season started last year
            }
        }

        const divStartCalendarDate = parseDate(`${seasonYear}-${div.starts_at}`);
        const divEndCalendarDate = parseDate(
            div.ends_at > div.starts_at
                ? `${seasonYear}-${div.ends_at}`
                : `${seasonYear + 1}-${div.ends_at}`
        );

        // within season
        if (
            startCalendarDate.compare(divStartCalendarDate) >= 0 &&
            startCalendarDate.compare(divEndCalendarDate) <= 0
        ) {
            return [divStartCalendarDate.toString(), divEndCalendarDate.toString()];
        }

        // keep track of closest next division in the current season/year if any
        if (
            startCalendarDate.compare(divStartCalendarDate) < 0 &&
            (closest === null || divStartCalendarDate.compare(closest[0]) < 0)
        ) {
            closest = [divStartCalendarDate, divEndCalendarDate];
        }

        // keep track of first division of the season/year
        if (first === null || first[0].compare(divStartCalendarDate) > 0) {
            first = [divStartCalendarDate, divEndCalendarDate];
        }
    }

    if (closest !== null) {
        // closest div in the future
        return [closest[0].toString(), closest[1].toString()];
    }

    if (first !== null) {
        // first div next year
        return [first[0].add(1, 'year').toString(), first[1].add(1, 'year').toString()];
    }

    return ['', ''];
};

export const isMobile = {
    Android() {
        return window.navigator.userAgent.match(/Android/i);
    },
    BlackBerry() {
        return window.navigator.userAgent.match(/BlackBerry/i);
    },
    iOS() {
        return window.navigator.userAgent.match(/iPhone|iPad|iPod/i);
    },
    Opera() {
        return window.navigator.userAgent.match(/Opera Mini/i);
    },
    Windows() {
        return window.navigator.userAgent.match(/IEMobile/i);
    },
    any() {
        return (
            isMobile.Android() ||
            isMobile.BlackBerry() ||
            isMobile.iOS() ||
            isMobile.Opera() ||
            isMobile.Windows()
        );
    },
};

// eslint-disable-next-line consistent-return
export const printBlob = (blob, fileName = 'file.pdf') => {
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // IE opens blobs this way:
        if (fileName) {
            window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
            window.navigator.msSaveOrOpenBlob(blob);
        }
    } else {
        // Other browsers use iframe printing:
        const url = window.URL.createObjectURL(blob);

        if (isMobile.any()) {
            return window.open(url, '_blank');
        }

        if (isFirefox()) {
            const downloadLink = document.createElement('a');
            downloadLink.target = '_blank';
            downloadLink.download = fileName;

            downloadLink.href = url;
            downloadLink.style.display = 'none';

            document.body.append(downloadLink);

            downloadLink.click();

            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(url);
        } else {
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            iframe.addEventListener(
                'load',
                () => {
                    setTimeout(() => {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                    }, 0);
                },
                false
            );

            iframe.setAttribute('src', url);
        }
    }
};

const printPDFUrl = (pdfUrl) => {
    if (!pdfUrl) return;
    axios
        .get(pdfUrl.replace(/\/pdf\b/, '/print'), {
            headers: { Authorization: SecurityHelper.getAuthHeader() },
            responseType: 'arraybuffer',
        })
        .then((response) =>
            printBlob(new Blob([response.data], { type: 'application/pdf' }))
        );
};

export const printPass = ({ downloadUrl, reservationId }) => {
    if (!downloadUrl && !reservationId) return;

    if (downloadUrl) {
        printPDFUrl(downloadUrl);
    } else if (process.env.API) {
        axios
            .get(`${process.env.API}/activitypass/passes/my-passes`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then(({ data: { passes } }) => {
                const pass = passes.find(({ id }) => id === reservationId);
                if (pass) {
                    printPDFUrl(pass.download_url);
                }
            });
    }
};

const { getWithAuth } = apiHelpers(SecurityHelper);

export const downloadPassPdf = (downloadUrl, location) =>
    getWithAuth(downloadUrl, { responseType: 'arraybuffer' })
        .then((response) => {
            const filename = location.replace(/\s/g, '-');
            const content = response.headers['content-type'];
            download(response.data, `${filename}.pdf`, content);
        })
        // eslint-disable-next-line no-console
        .catch((error) => console.log(error));

export const isOffline = (passTypes) => {
    if (!passTypes?.length) {
        return false;
    }

    const offlinePassTypes = passTypes.filter((item) => {
        return item?.duration.endsWith('-offline');
    });

    if (passTypes?.length === offlinePassTypes?.length) {
        return true;
    }

    return false;
};

export const isDaily = (passType) => {
    return passType?.duration?.endsWith('-day');
};

export const isFacilityDeleted = (facility) => {
    const { deleted_at: isDeleted = '' } = facility;
    return !!isDeleted;
};

export const isFacilityActive = (facility) => {
    if (isFacilityDeleted(facility)) {
        return false;
    }
    const { active_on: activeOn } = facility;
    if (!isValidDateFormat(activeOn)) {
        return false;
    }
    const date = parseAbsolute(activeOn);
    return date.compare(now()) <= 0;
};

export const isInteragencyAccessPass = (interagencyPassType = '') => {
    return INTERAGENCY_DISCOUNT_PASSES[0] === interagencyPassType;
};

export const isInteragencyAnnualSeniorPass = (interagencyPassType = '') => {
    return INTERAGENCY_DISCOUNT_PASSES[1] === interagencyPassType;
};

export const isInteragencyLifetimeSeniorPass = (interagencyPassType = '') => {
    return INTERAGENCY_DISCOUNT_PASSES[2] === interagencyPassType;
};

export const isPassNumberValid = (interagencyPassType = '', passNumber = '') => {
    if (!passNumber) {
        return true;
    }

    const thirdChar = passNumber?.toLowerCase()?.charAt(2);

    if (
        isInteragencyAnnualSeniorPass(interagencyPassType) &&
        !['6', 'f'].includes(thirdChar)
    ) {
        return false;
    }
    if (
        isInteragencyLifetimeSeniorPass(interagencyPassType) &&
        !['2', 'b'].includes(thirdChar)
    ) {
        return false;
    }
    if (isInteragencyAccessPass(interagencyPassType) && !['3', 'c'].includes(thirdChar)) {
        return false;
    }

    return true;
};

export const isPassTypeValid = (interagencyPassType = '', passNumber = '') => {
    if (passNumber) {
        return !!interagencyPassType;
    }

    return true;
};

export const useError = (error, defaultErr) => {
    const errorStr = get(error, 'message', '');
    const errorDataStr = errorStr?.split(' ')?.filter((item) => {
        const val = item.substring(1);
        if (val !== val.toLowerCase()) {
            return true;
        }
        // eslint-disable-next-line
        if (val.match(/[^a-zA-Z0-9\.,:]/)) {
            return true;
        }
        return false;
    });

    const isReadableError = isEmpty(errorDataStr);

    if (isReadableError) {
        return errorStr;
    }
    return defaultErr;
};

export const googlePassURL = (passID) => {
    if (process.env.API) {
        return `${process.env.API}/activitypass/passes/${passID}/googlepass`;
    }
    return `/api/activitypass/passes/${passID}/googlepass`;
};

// cspell:ignore lowerbound, activitypass
